import { computed, ref, watch, type Ref } from 'vue'
import { useQuery, useQueryClient } from '@tanstack/vue-query'
import { mediaApi, workOrdersApi } from '@ck/api-client'
import { compressImage } from '../lib/compressImage'

// A capa (foto de perfil do veículo) é um dado canônico do backend: cada OS tem no máximo uma
// (MediaAsset Kind='cover', garantido por índice único). Lemos o coverMediaId do work-order —
// fonte única, sem convenção de cliente — e baixamos só esse blob. O upload usa o endpoint
// dedicado, que comprime no servidor e substitui a capa anterior atomicamente.
export function useWorkOrderCover(workOrderId: Ref<string | undefined>) {
  const queryClient = useQueryClient()
  const workOrderQuery = useQuery(computed(() => ({
    queryKey: ['work-order', workOrderId.value],
    queryFn: () => workOrdersApi.get(workOrderId.value!),
    enabled: !!workOrderId.value
  })))
  const coverMediaId = computed(() => workOrderQuery.data.value?.coverMediaId ?? null)

  const coverUrl = ref<string | null>(null)
  watch(coverMediaId, async (id, _previous, onCleanup) => {
    if (!id) { coverUrl.value = null; return }
    let url: string | null = null
    try { url = URL.createObjectURL(await mediaApi.fetchFile(id)) } catch { url = null }
    coverUrl.value = url
    onCleanup(() => { if (url) URL.revokeObjectURL(url) })
  }, { immediate: true })

  const uploading = ref(false)
  const uploadError = ref(false)
  async function uploadCover(file: File) {
    if (!workOrderId.value) return
    uploading.value = true
    uploadError.value = false
    try {
      // Compressão no cliente só reduz banda; o servidor reencoda e é a autoridade.
      const compressed = await compressImage(file)
      await workOrdersApi.uploadCover(workOrderId.value, compressed)
      await queryClient.invalidateQueries({ queryKey: ['work-order', workOrderId.value] })
      await queryClient.invalidateQueries({ queryKey: ['work-orders'] })
    } catch {
      uploadError.value = true
    } finally {
      uploading.value = false
    }
  }

  return { coverMediaId, coverUrl, uploadCover, uploading, uploadError }
}
